import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { calculateMultipleBoundingBoxes } from './BoundingBoxCalculator';
import { generateYOLOLabel } from './YOLOLabelExporter';
import { calculateMultiplePolygons } from './PolygonCalculator';
import { generateYOLOSegmentationLabel } from './SegmentationLabelExporter';

export const CanvasContent = forwardRef((props, ref) => {
    const { 
        objectSpawners = [], // Array von { ref, classId, type }
        imageWidth = 640,
        imageHeight = 640,
        exportYOLO = true,
        annotationType = 'boundingBox', // 'boundingBox' oder 'segmentation'
        randomizers = {}, // Randomizer-Konfiguration
        onExportStateChange = null, // Callback für Export-Status
        onFrameChange = null // Callback für Frame-Änderungen
    } = props;
  
    const { gl, scene, camera } = useThree();
    const frameRef = useRef(0);
    const canvasesRef = useRef([]); // Store canvases instead of images
    const labelsRef = useRef([]); // Store YOLO labels
    const [isZipping, setIsZipping] = useState(false);
    const zippingRef = useRef(false);
    const [startSaving, setStartSaving] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [maxFramesLocal, setMaxFramesLocal] = useState(10);

    useImperativeHandle(ref, () => {
        return {
          startSavingFrames(maxFrames) {
              setMaxFramesLocal(maxFrames)
              setStartSaving(true)
              setIsExporting(true)
              frameRef.current = 0
              canvasesRef.current = []
              labelsRef.current = []
              // Informiere über Frame 0 beim Start - WICHTIG für Environment-Randomisierung
              if (onFrameChange) {
                  console.log('CanvasContent: Starting export, notifying frame 0');
                  onFrameChange(0);
              }
              if (onExportStateChange) onExportStateChange(true)
          }
        };
      }, [onExportStateChange, onFrameChange]);

    const saveCanvas = (canvas) => {
        // Clone the canvas and store it
        let newCanvas = document.createElement('canvas');
        let context = newCanvas.getContext('2d');
        newCanvas.width = canvas.width;
        newCanvas.height = canvas.height;
        context.drawImage(canvas, 0, 0);
        canvasesRef.current.push(newCanvas);

        // Berechne Bounding Boxes und generiere YOLO Labels
        if (exportYOLO && objectSpawners.length > 0) {
            // Verwende die tatsächliche Canvas-Größe für Berechnung
            // canvas.width/height sind die Render-Dimensionen
            const actualWidth = canvas.width || imageWidth;
            const actualHeight = canvas.height || imageHeight;
            
            let yoloLabel = '';
            
            if (annotationType === 'segmentation') {
                // Instance Segmentation: Berechne Polygone
                const allPolygons = [];
                
                objectSpawners.forEach(spawner => {
                    if (spawner.ref && spawner.ref.current) {
                        try {
                            const objects = spawner.ref.current.getObjects();
                            if (objects && objects.length > 0) {
                            // Berechne Polygone (Kamera-Matrizen sind bereits aktualisiert)
                            // WICHTIG: Verwendet die gleichen Standardwerte wie die Debug-Visualisierung
                            // für identische Ansicht im Editor und beim Export
                            const polygons = calculateMultiplePolygons(
                                objects,
                                camera,
                                actualWidth,
                                actualHeight
                            );
                                
                                // Füge classId hinzu
                                const polygonsWithClass = polygons.map(poly => ({
                                    ...poly,
                                    classId: spawner.classId !== undefined ? spawner.classId : 0
                                }));
                                
                                allPolygons.push(...polygonsWithClass);
                            }
                        } catch (error) {
                            console.warn('Error calculating polygons for export:', error);
                        }
                    }
                });

                // Generiere YOLO Segmentation Label
                yoloLabel = generateYOLOSegmentationLabel(allPolygons, actualWidth, actualHeight);
            } else {
                // Object Detection: Berechne Bounding Boxes
                const allBoundingBoxes = [];
                
                objectSpawners.forEach(spawner => {
                    if (spawner.ref && spawner.ref.current) {
                        try {
                            const objects = spawner.ref.current.getObjects();
                            if (objects && objects.length > 0) {
                                // Berechne Bounding Boxes (Kamera-Matrizen sind bereits aktualisiert)
                                const bboxes = calculateMultipleBoundingBoxes(
                                    objects,
                                    camera,
                                    actualWidth,
                                    actualHeight
                                );
                                
                                // Füge classId hinzu
                                const bboxesWithClass = bboxes.map(bbox => ({
                                    ...bbox,
                                    classId: spawner.classId !== undefined ? spawner.classId : 0
                                }));
                                
                                allBoundingBoxes.push(...bboxesWithClass);
                            }
                        } catch (error) {
                            console.warn('Error calculating bounding boxes for export:', error);
                        }
                    }
                });

                // Generiere YOLO Label
                yoloLabel = generateYOLOLabel(allBoundingBoxes, actualWidth, actualHeight);
            }
            
            labelsRef.current.push(yoloLabel);
        } else {
            labelsRef.current.push(''); // Leeres Label wenn keine Objekte
        }
    };

    const zipAndDownload = async () => {
        zippingRef.current = true;
        const zipArchive = new JSZip();
        
        // Erstelle YOLO-Dataset-Struktur
        const imagesFolder = zipArchive.folder('images');
        const labelsFolder = zipArchive.folder('labels');

        // Convert each stored canvas to an image and add it to the zip
        for (let i = 0; i < canvasesRef.current.length; i++) {
            let canvas = canvasesRef.current[i];
            const frameNumber = String(i).padStart(6, '0');
            const filename = `image_${frameNumber}`;
            
            // Speichere Bild
            let imageData = await new Promise(resolve =>
                canvas.toBlob(blob => {
                    let reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result.split(',')[1]);
                    reader.readAsDataURL(blob);
                }, 'image/jpeg', 0.8)
            );
            imagesFolder.file(`${filename}.jpg`, imageData, { base64: true });
            
            // Speichere Label (wenn YOLO Export aktiviert)
            if (exportYOLO && labelsRef.current[i] !== undefined) {
                const labelContent = labelsRef.current[i] || '';
                labelsFolder.file(`${filename}.txt`, labelContent);
            }
        }

        // Erstelle classes.txt wenn Klassen vorhanden sind
        if (exportYOLO && props.classes && props.classes.length > 0) {
            const classesContent = props.classes.map(c => typeof c === 'string' ? c : c.name).join('\n');
            zipArchive.file('classes.txt', classesContent);
        }

        zipArchive.generateAsync({ type: "blob" })
            .then(function (content) {
                try {
                    const zipName = exportYOLO ? 'yolo_dataset.zip' : 'images.zip';
                    saveAs(content, zipName);
                } catch (error) {
                   console.log(error);
                }
                setStartSaving(false);
                setIsExporting(false);
                if (onExportStateChange) onExportStateChange(false);
                zippingRef.current = false;
                setIsZipping(false);
                frameRef.current = 0;
                canvasesRef.current = [];
                labelsRef.current = [];
            }).catch((error) => {
                console.log(error);
            });
    };

    useFrame((state, delta) => {
        // WICHTIG: Nur während Export aktiv sein, sonst nichts tun
        // React Three Fiber rendert automatisch wenn frameloop="always" gesetzt ist
        if (startSaving && frameRef.current < maxFramesLocal) {
            // WICHTIG: Randomizer werden hier pro Frame angewendet
            // Die Komponenten erhalten isExporting=true, um zu wissen, dass sie randomisieren sollen
            
            // Informiere über Frame-Änderung für Environment-Randomisierung VOR dem Rendering
            // Wichtig: Muss VOR dem Rendering sein, damit das neue Environment geladen werden kann
            // WICHTIG: Verwende die aktuelle Frame-Nummer BEVOR sie erhöht wird
            if (onFrameChange) {
                onFrameChange(frameRef.current);
            }
            
            // Stelle sicher, dass Kamera-Matrizen aktuell sind (nur einmal pro Frame)
            camera.updateMatrixWorld(true);
            camera.updateProjectionMatrix();
            
            // Render die Szene manuell für Export (Randomizer werden in den Komponenten angewendet)
            gl.render(scene, camera);
            
            // Speichere Frame und Label
            saveCanvas(gl.domElement);
            frameRef.current++;
        } else if (startSaving && frameRef.current >= maxFramesLocal && !isZipping && !zippingRef.current) {
            setIsExporting(false);
            if (onExportStateChange) onExportStateChange(false);
            setIsZipping(true);
            zipAndDownload();
        }
        // Wenn kein Export läuft: Nichts tun - React Three Fiber rendert automatisch
    }, startSaving ? 1 : 0); // Priority 1 nur wenn Export läuft, sonst normale Priorität

    return null;
});
